// src/components/transport/SectionTransportEleve.jsx
// ============================================================================
// L'abonnement au bus d'un élève : où il en est, et le changer (lots 3a et 3a bis)
// ============================================================================
//
// Montré dans le dossier de l'élève (onglet Bus scolaire) et au guichet
// (paiement du bus). « Changer » ouvre le formulaire À LA PLACE de la lecture,
// dans la fenêtre où l'on est (dossier, guichet) : jamais une seconde fenêtre
// par-dessus (demande de Daniel du 04/11/2026 - empilée sur le dossier, elle
// s'ouvrait même dessous). Sans bus, au mois, au ticket, et le trajet (zone,
// lieu de prise en charge). Les effets sur les mensualités sont dits AVANT de
// valider :
//   - au mois : ses mensualités de bus sont créées à partir du premier mois ;
//   - un abonné au mois qui change de sens ou de zone : à partir du mois
//     d'effet, les mois d'avant gardent leur prix ;
//   - au ticket ou sans bus : les mensualités NON PAYÉES à partir du mois
//     d'effet sont retirées ; celles d'avant restent dues, un mois payé (même
//     en partie) n'est jamais touché.
// « Demander un autre prix », de même en place, envoie une dérogation au Centre de Support :
// une autre personne que le demandeur l'approuve (décision du 03/11/2026).
// Le serveur décide et rend ce qu'il a fait ; la section se relit.
//
// Changer ou demander un prix demande ELEVES.TRANSPORT.GERER : sans elle, la
// section se lit sans bouton.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Bus, CheckCircle2, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { BOUTON_CONTOUR, BOUTON_PLEIN } from '../ui/boutons';
import ListeDeroulante from '../ui/ListeDeroulante';
import LoadingSpinner from '../LoadingSpinner';
import PorteurNotification from '../ui/PorteurNotification';
import { SENS, fcfa, transport } from './apiTransport';
import ChoixTransport, { transportIncomplet } from './ChoixTransport';

const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const MOTIF_MIN = 10;

/** Un formulaire en place : en-tête à pastille, corps, pied à droite. */
const Edition = ({ icone: Icone, titre, description, pied, children }) => (
  <div className="overflow-hidden rounded-lg border border-[#246990]/30 bg-white">
    <div className="flex items-center gap-3 border-b border-gray-100 bg-[#eef7fb]/60 px-4 py-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
        <Icone className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-900">{titre}</h3>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="space-y-4 p-4">{children}</div>
    <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3">{pied}</div>
  </div>
);

const phraseAbonnement = (etat) => {
  const a = etat.abonnement;
  if (!a || !a.actif) return 'Ne prend pas le bus.';
  if (a.regime === 'ponctuel') {
    return `Au ticket : journée ${fcfa(etat.tickets.journee)}, demi-journée ${fcfa(etat.tickets.demi_journee)}.`;
  }
  const montant = etat.prix_du_mois ?? (a.sens === 'aller' || a.sens === 'retour' ? etat.montants.un_sens : etat.montants.aller_retour);
  return `Au mois, ${(SENS[a.sens] || '').toLowerCase()}${a.mois_depart ? `, depuis ${a.mois_depart}` : ''} : ${fcfa(montant)} par mois.`;
};

const phraseTrajet = (trajet) => {
  if (!trajet || (!trajet.zone_nom && !trajet.lieu)) return null;
  return [trajet.zone_nom, trajet.lieu, trajet.repere ? `(${trajet.repere})` : null].filter(Boolean).join(' - ');
};

const valeurDe = (etat) => {
  const a = etat?.abonnement;
  const trajet = {
    zone_id: etat?.trajet?.zone_id ? String(etat.trajet.zone_id) : '',
    lieu: etat?.trajet?.lieu || '',
    repere: etat?.trajet?.repere || ''
  };
  if (!a || !a.actif) return { regime: 'aucun', sens: 'aller_retour', mois_depart_id: '', ...trajet };
  return { regime: a.regime, sens: a.sens || 'aller_retour', mois_depart_id: a.mois_depart_id ? String(a.mois_depart_id) : '', ...trajet };
};

const memeTrajet = (x, y) => String(x.zone_id || '') === String(y.zone_id || '')
  && String(x.lieu || '').trim() === String(y.lieu || '').trim()
  && String(x.repere || '').trim() === String(y.repere || '').trim();

const ChoixDuMois = ({ id, etiquette, mois, valeur, onChange, disabled, children }) => (
  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
    <label htmlFor={id} className="block text-sm font-medium text-amber-900">{etiquette}</label>
    <ListeDeroulante
      id={id}
      valeur={valeur}
      onChange={onChange}
      desactive={disabled}
      options={[{ valeur: '', libelle: 'Mois en cours' }, ...mois.map((m) => ({ valeur: String(m.id), libelle: m.nom }))]}
    />
    <p className="text-xs leading-relaxed text-amber-900">{children}</p>
  </div>
);

const EditionAbonnement = ({ etat, onFermer, onFait }) => {
  const [valeur, setValeur] = useState(() => valeurDe(etat));
  const [effet, setEffet] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const avant = valeurDe(etat);
  const quitteLeMois = avant.regime === 'mensuel' && valeur.regime !== 'mensuel';
  const changeDansLeMois = avant.regime === 'mensuel' && valeur.regime === 'mensuel'
    && (valeur.sens !== avant.sens || String(valeur.zone_id || '') !== String(avant.zone_id || ''));
  const inchange = valeur.regime === avant.regime && memeTrajet(valeur, avant)
    && (valeur.regime !== 'mensuel' || (valeur.sens === avant.sens && String(valeur.mois_depart_id || '') === String(avant.mois_depart_id || '')));

  const enregistrer = async () => {
    if (envoi || inchange) return;
    const manque = transportIncomplet(valeur, { zones: etat.zones });
    if (manque) { setErreur(manque); return; }
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await transport.abonner(etat.eleve.id, { ...valeur, mois_effet_id: (quitteLeMois || changeDansLeMois) ? (effet || undefined) : undefined });
      onFait(r);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Edition
      icone={Bus}
      titre="Changer le bus scolaire"
      description={`Aujourd'hui : ${phraseAbonnement(etat).replace(/\.$/, '').toLowerCase()}.`}
      pied={(
        <>
          <Button size="default" type="button" className={`${BOUTON_CONTOUR} ${BOUTON_PIED}`} onClick={onFermer} disabled={envoi}>Annuler</Button>
          <Button size="default" type="button" className={`${BOUTON_PIED} ${BOUTON_PLEIN}`} onClick={enregistrer} disabled={envoi || inchange}>
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Enregistrer
          </Button>
        </>
      )}
    >
          <PorteurNotification notification={erreur ? { message: erreur, type: 'error' } : null} onFermer={() => setErreur(null)} />
          <ChoixTransport
            tarif={etat.tarif_classe}
            reglages={etat.reglages}
            zones={etat.zones}
            mois={etat.mois}
            classe={etat.eleve.classe}
            valeur={valeur}
            onChange={setValeur}
            disabled={envoi}
            encadre={false}
          />
          {quitteLeMois && (
            <ChoixDuMois id="transport-effet" etiquette="À partir de quel mois ?" mois={etat.mois} valeur={effet} onChange={setEffet} disabled={envoi}>
              Les mensualités de bus non payées à partir de ce mois seront retirées. Celles d'avant restent dues ; un mois
              déjà payé, même en partie, n'est jamais touché.
            </ChoixDuMois>
          )}
          {changeDansLeMois && (
            <ChoixDuMois id="transport-effet-changement" etiquette="Le nouveau prix vaut à partir de quel mois ?" mois={etat.mois} valeur={effet} onChange={setEffet} disabled={envoi}>
              Les mensualités non payées à partir de ce mois prennent le nouveau prix ; celles d'avant gardent le leur. Un
              mois déjà payé, même en partie, n'est jamais touché.
            </ChoixDuMois>
          )}
          {valeur.regime === 'mensuel' && avant.regime !== 'mensuel' && (
            <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs leading-relaxed text-gray-700">
              Ses mensualités de bus seront créées à partir du premier mois choisi. Un mois déjà payé garde son montant.
            </p>
          )}
    </Edition>
  );
};

const EditionDerogation = ({ etat, onFermer, onFait }) => {
  const enVigueur = etat.derogation_en_vigueur;
  const [retour, setRetour] = useState(false);
  const [montant, setMontant] = useState('');
  const [effet, setEffet] = useState(etat.mois_par_defaut_id ? String(etat.mois_par_defaut_id) : '');
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const a = etat.abonnement;
  const grille = a?.sens === 'aller' || a?.sens === 'retour' ? etat.montants.un_sens : etat.montants.aller_retour;
  const libelleGrille = etat.mode === 'zones' ? 'tarif de sa zone' : 'tarif de la classe';
  const motifOk = motif.trim().length >= MOTIF_MIN;
  const montantOk = retour || Number(montant) > 0;

  const envoyer = async () => {
    if (envoi || !motifOk || !montantOk) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await transport.demanderDerogation(etat.eleve.id, {
        ...(retour ? { retour_grille: true } : { montant_mensuel: Number(montant) }),
        mois_effet_id: effet || undefined,
        motif: motif.trim()
      });
      onFait(r);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Edition
      icone={Send}
      titre="Demander un autre prix de bus"
      description={`Aujourd'hui : ${fcfa(etat.prix_du_mois ?? grille)} par mois${enVigueur ? ' (prix dérogatoire)' : ''}. Au ${libelleGrille} : ${fcfa(grille)}.`}
      pied={(
        <>
          <Button size="default" type="button" className={`${BOUTON_CONTOUR} ${BOUTON_PIED}`} onClick={onFermer} disabled={envoi}>Annuler</Button>
          <Button size="default" type="button" className={`${BOUTON_PIED} ${BOUTON_PLEIN}`} onClick={envoyer} disabled={envoi || !motifOk || !montantOk}>
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer la demande
          </Button>
        </>
      )}
    >
          <PorteurNotification notification={erreur ? { message: erreur, type: 'error' } : null} onFermer={() => setErreur(null)} />
          {enVigueur && (
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Demande">
              {[
                { cle: false, titre: 'Un nouveau prix', texte: 'Remplace le prix dérogatoire actuel.' },
                { cle: true, titre: `Revenir au ${libelleGrille}`, texte: `${fcfa(grille)} par mois.` }
              ].map((o) => (
                <button
                  key={String(o.cle)}
                  type="button"
                  role="radio"
                  aria-checked={retour === o.cle}
                  disabled={envoi}
                  onClick={() => setRetour(o.cle)}
                  className={`rounded-lg border p-3 text-left transition ${retour === o.cle ? 'border-[#246990] bg-[#eef7fb] ring-2 ring-[#246990]/15' : 'border-gray-200 bg-white hover:border-[#246990]/50'}`}
                >
                  <span className="block text-sm font-semibold text-gray-900">{o.titre}</span>
                  <span className="mt-0.5 block text-xs text-gray-500">{o.texte}</span>
                </button>
              ))}
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {!retour && (
              <div>
                <label htmlFor="derogation-montant" className="mb-1 block text-sm text-gray-700">Prix mensuel demandé</label>
                <div className="relative">
                  <input
                    id="derogation-montant"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`${CHAMP} pr-16 text-right`}
                    value={montant}
                    disabled={envoi}
                    onChange={(e) => setMontant(e.target.value.replace(/[^\d]/g, ''))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                </div>
              </div>
            )}
            <div>
              <label htmlFor="derogation-effet" className="mb-1 block text-sm text-gray-700">À partir de</label>
              <ListeDeroulante
                id="derogation-effet"
                valeur={effet}
                onChange={setEffet}
                desactive={envoi}
                options={etat.mois.map((m) => ({ valeur: String(m.id), libelle: m.nom }))}
              />
            </div>
          </div>
          <div>
            <label htmlFor="derogation-motif" className="mb-1 block text-sm text-gray-700">Motif (obligatoire)</label>
            <textarea
              id="derogation-motif"
              rows={3}
              maxLength={500}
              className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              value={motif}
              disabled={envoi}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Pourquoi ce prix : arrangement de la direction, situation de la famille..."
            />
            <p className="mt-1 text-xs text-gray-500">{motif.trim().length} / 500 - {MOTIF_MIN} caractères au moins.</p>
          </div>
          <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs leading-relaxed text-gray-700">
            La demande part au Centre de Support : une autre personne du groupe validateur doit l'approuver, jamais celle
            qui l'a envoyée. Une fois approuvée, les mensualités non payées à partir de ce mois prennent ce prix ; un mois
            déjà payé, même en partie, ne bouge pas. Sa zone reste celle notée.
          </p>
    </Edition>
  );
};

/** Le bilan rendu par le serveur, en une phrase. */
const phraseBilan = (lignes = {}) => [
  lignes.creees?.length ? `${lignes.creees.length} mensualité${lignes.creees.length > 1 ? 's' : ''} créée${lignes.creees.length > 1 ? 's' : ''}` : null,
  lignes.mises_a_jour?.length ? `${lignes.mises_a_jour.length} mise${lignes.mises_a_jour.length > 1 ? 's' : ''} à jour` : null,
  lignes.retirees?.length ? `${lignes.retirees.length} retirée${lignes.retirees.length > 1 ? 's' : ''}` : null,
  lignes.conservees?.length ? `${lignes.conservees.length} gardée${lignes.conservees.length > 1 ? 's' : ''} telle${lignes.conservees.length > 1 ? 's' : ''} quelle${lignes.conservees.length > 1 ? 's' : ''} (payée ou non scolarisé)` : null
].filter(Boolean).join(', ');

const SectionTransportEleve = ({ eleveId, onChange, compact = false }) => {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [fenetre, setFenetre] = useState(null);
  const [message, setMessage] = useState(null);

  const charger = useCallback(async () => {
    if (!eleveId) return;
    try {
      setEtat(await transport.eleve(eleveId));
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    }
  }, [eleveId]);

  useEffect(() => { setEtat(null); setMessage(null); charger(); }, [charger]);

  if (erreur) return <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</p>;
  if (!etat) return <LoadingSpinner message="Chargement du bus..." size="sm" />;
  if (!etat.installe) return null;

  const mensualites = etat.mensualites || [];
  const avecZones = mensualites.some((m) => m.zone);
  const trajet = phraseTrajet(etat.trajet);
  const enAttente = (etat.derogations || []).find((d) => d.statut === 'en_attente') || null;
  const accordee = etat.derogation_en_vigueur
    ? (etat.derogations || []).find((d) => d.id === etat.derogation_en_vigueur.id) || null
    : null;
  const abonneAuMois = etat.abonnement?.actif && etat.abonnement.regime === 'mensuel';
  const peutDemander = etat.droits?.gerer && etat.zones_installees && abonneAuMois && !enAttente;
  // L'état rendu par un enregistrement ne porte pas les droits : on garde ceux lus.
  const relire = (r) => { if (r?.etat) setEtat({ ...r.etat, droits: etat.droits }); else charger(); };

  if (fenetre === 'abonnement') {
    return (
      <EditionAbonnement
        etat={etat}
        onFermer={() => setFenetre(null)}
        onFait={(r) => {
          setFenetre(null);
          setMessage(phraseBilan(r.lignes) ? `Enregistré : ${phraseBilan(r.lignes)}.` : 'Enregistré.');
          relire(r);
          if (onChange) onChange(r);
        }}
      />
    );
  }
  if (fenetre === 'derogation') {
    return (
      <EditionDerogation
        etat={etat}
        onFermer={() => setFenetre(null)}
        onFait={(r) => {
          setFenetre(null);
          setMessage(`Demande n°${r.request?.id} envoyée au Centre de Support.`);
          relire(r);
        }}
      />
    );
  }

  return (
    <section className={`rounded-lg border border-gray-200 bg-white ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Bus scolaire</h3>
          <p className="mt-0.5 text-sm text-gray-600">{phraseAbonnement(etat)}</p>
          {trajet && <p className="mt-0.5 text-xs text-gray-500">Trajet : {trajet}</p>}
          {accordee && (
            <p className="mt-1 text-xs font-medium text-[#246990]">
              Prix dérogatoire : {fcfa(accordee.montant_mensuel)} par mois depuis {accordee.mois_effet_nom}
              {accordee.validateur_nom ? `, approuvé par ${accordee.validateur_nom}` : ''}{accordee.decidee_le ? ` le ${accordee.decidee_le}` : ''}.
            </p>
          )}
          {message && <p className="mt-1 text-xs font-medium text-emerald-700">{message}</p>}
        </div>
        {etat.droits?.gerer && (
          <div className="flex flex-wrap gap-2">
            {peutDemander && (
              <Button className={BOUTON_CONTOUR} type="button" size="sm" onClick={() => setFenetre('derogation')}>Demander un autre prix</Button>
            )}
            <Button className={BOUTON_CONTOUR} type="button" size="sm" onClick={() => setFenetre('abonnement')}>Changer</Button>
          </div>
        )}
      </div>
      {enAttente && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
          {enAttente.montant_mensuel === null ? 'Retour au tarif de la zone demandé' : `Prix demandé : ${fcfa(enAttente.montant_mensuel)} par mois`}
          {' '}à partir de {enAttente.mois_effet_nom}, le {enAttente.demandee_le}{enAttente.demandeur_nom ? ` par ${enAttente.demandeur_nom}` : ''}
          {' '}(demande n°{enAttente.support_request_id}) : en attente au Centre de Support.
        </p>
      )}
      {!compact && mensualites.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3">Mois</th>
                {avecZones && <th className="py-2 pr-3">Zone</th>}
                <th className="py-2 pr-3 text-right">Dû</th>
                <th className="py-2 pr-3 text-right">Payé</th>
                <th className="py-2">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mensualites.map((m) => (
                <tr key={m.mois_id}>
                  <td className="py-2 pr-3 text-gray-800">{m.mois}</td>
                  {avecZones && <td className="py-2 pr-3 text-gray-600">{m.zone || '-'}</td>}
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                    {fcfa(m.montant_attendu)}{m.derogation ? <span className="ml-1 text-[11px] text-[#246990]">dérogation</span> : null}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{fcfa(m.montant_paye)}</td>
                  <td className="py-2 text-gray-600">{m.statut}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default SectionTransportEleve;
